import React, { useEffect, useState } from 'react'

import AdjusterSelecter from '../AdjusterSelecter'
import DatePicker from '../controls/DatePicker'
import { ErrorMessage, Field } from '../controls/FormikWrappers'
import ReferralSourceSelector from '../ReferralSourceSelecter'

import 'react-datepicker/dist/react-datepicker.css'

const ReferralForm = ({
  caseTypeName,
  referralSources,
  onAddNewReferralSource,
  adjusters,
  onAddNewAdjuster,
  onEditAdjuster,
  onEditReferralSource,
  isOngoingCaseMode = false,
  onAddOngoingReportFiles,
  ...props
}) => {
  const hasOngoingValues = Boolean(
    props.values?.skipInitialReport ||
      Number(props.values?.startingProgressReportNumber) > 1 ||
      props.values?.startingReportDate,
  )
  const [isOngoingSectionExpanded, setIsOngoingSectionExpanded] = useState(
    Boolean(isOngoingCaseMode || hasOngoingValues),
  )

  useEffect(() => {
    if (isOngoingCaseMode || hasOngoingValues) {
      setIsOngoingSectionExpanded(true)
    }
  }, [isOngoingCaseMode, hasOngoingValues])
  return (
    <React.Fragment>
      {/* Case Type — read-only, already selected on the picker page */}
      <div className="form-row">
        <div className="col-md-4">
          <div className="form-group">
            <label className="form-label mt-3">Case Type</label>
            <p className="form-control-plaintext font-weight-semibold">{caseTypeName || '—'}</p>
          </div>
        </div>
      </div>

      <h4 className="form-heading mt-4">Claimant</h4>
      <p className="text-danger small">Fields marked with * are required to create a case.</p>
      <p className="text-muted small">Date of Referral and Adjuster are additionally required to activate the case.</p>

      {/* Claimant name */}
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="client.firstName">
              First Name*
            </label>
            <Field
              type="text"
              id="client.firstName"
              className="form-control"
              placeholder="First Name"
              name="client.firstName"
            />
            <ErrorMessage name="client.firstName" />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="client.lastName">
              Last Name*
            </label>
            <Field
              type="text"
              id="client.lastName"
              className="form-control"
              placeholder="Last Name"
              name="client.lastName"
            />
            <ErrorMessage name="client.lastName" />
          </div>
        </div>
      </div>

      {/* DOB and Date of Injury — both required to create a case */}
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="client.birthdate">
              Date of Birth*
            </label>
            <Field name="client.birthdate">
              {({ field }) => <DatePicker id="client.birthdate" dataFormat="YYYY-MM-DD" {...field} />}
            </Field>
            <ErrorMessage name="client.birthdate" />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="injury.date">
              Date of Injury*
            </label>
            <Field name="injury.date">
              {({ field }) => <DatePicker id="injury.date" dataFormat="YYYY-MM-DD" {...field} />}
            </Field>
            <ErrorMessage name="injury.date" />
          </div>
        </div>
      </div>

      <h4 className="form-heading mt-4">Referral</h4>
      <p className="text-muted small">Date of Referral and Adjuster are required to activate the case.</p>

      {/* Date of Referral */}
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="referral.date">
              Date of Referral
            </label>
            <Field name="referral.date">
              {({ field }) => <DatePicker id="referral.date" dataFormat="YYYY-MM-DD" {...field} />}
            </Field>
            <ErrorMessage name="referral.date" />
          </div>
        </div>
      </div>

      {/* Referral Source */}
      <div className="form-row">
        <div className="col-md-6">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="referral.sourceId">
              Referral Source
            </label>
            <Field name="referral.sourceId">
              {({ field, form }) => (
                <ReferralSourceSelector
                  referralSources={referralSources}
                  onEditRequested={onEditReferralSource}
                  onAddRequested={onAddNewReferralSource}
                  id="referral.sourceId"
                  {...field}
                  onChange={e => {
                    field.onChange(e)
                    setTimeout(() => form.setFieldValue('referral.adjusterId', null), 0)
                  }}
                  isAdmin={true}
                  isClearable={true}
                />
              )}
            </Field>
            <ErrorMessage name="referral.sourceId" />
          </div>
        </div>
      </div>

      {/* Adjuster */}
      <div className="form-row">
        <div className="col-md-6">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="referral.adjusterId">
              Adjuster
            </label>
            <Field name="referral.adjusterId">
              {({ field, form }) => {
                const sourceId = form.values.referral?.sourceId
                const filteredAdjusters = adjusters.filter(x => {
                  if (sourceId) {
                    // eslint-disable-next-line eqeqeq
                    return x.referralSourceId == sourceId || x.referralSourceId == null
                  }
                  return true
                })
                return (
                  <AdjusterSelecter
                    key={sourceId || 'default'}
                    adjusters={filteredAdjusters}
                    onEditRequested={onEditAdjuster}
                    onAddRequested={onAddNewAdjuster}
                    id="referral.adjusterId"
                    {...field}
                    onChange={e => {
                      field.onChange(e)
                      const selectedId = e.target.value
                      if (selectedId) {
                        // eslint-disable-next-line eqeqeq
                        const selectedAdj = adjusters.find(x => x.id == selectedId)
                        if (selectedAdj && selectedAdj.referralSourceId) {
                          form.setFieldValue('referral.sourceId', selectedAdj.referralSourceId)
                        }
                      }
                    }}
                    isAdmin={true}
                  />
                )
              }}
            </Field>
            <ErrorMessage name="referral.adjusterId" />
          </div>
        </div>
      </div>

      {/* Claim Number & Policy Number — explicitly separated */}
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="referral.claimNumber">
              Claim Number
            </label>
            <Field type="text" name="referral.claimNumber" className="form-control" id="referral.claimNumber" />
            <ErrorMessage name="referral.claimNumber" />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="referral.policyNumber">
              Policy Number
            </label>
            <Field type="text" name="referral.policyNumber" className="form-control" id="referral.policyNumber" />
            <ErrorMessage name="referral.policyNumber" />
          </div>
        </div>
      </div>

      {/* Intake Ongoing Case (collapsible) */}
      <div className="mt-4 pt-2 border-top">
        <button
          type="button"
          className="btn btn-link p-0 text-left d-flex align-items-center text-decoration-none"
          onClick={() => setIsOngoingSectionExpanded(prev => !prev)}
          aria-expanded={isOngoingSectionExpanded}
          id="toggleIntakeOngoingCaseBtn"
        >
          <i className={`fas fa-chevron-${isOngoingSectionExpanded ? 'down' : 'right'} mr-2 text-primary`} />
          <h4 className="form-heading mb-0">Intake Ongoing Case</h4>
          {hasOngoingValues && !isOngoingSectionExpanded && <span className="badge badge-info ml-2">Configured</span>}
        </button>
        <p className="text-muted small mb-2 mt-1">
          Configure report numbering and start dates for an active case that already has an Initial Case Report or
          Progress Reports.
        </p>

        {isOngoingSectionExpanded && (
          <div className="pl-3 border-left mt-2">
            <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center justify-content-between flex-wrap">
              <div className="small mr-3">
                <i className="fas fa-magic mr-1" />
                <strong>Tip:</strong> Uploading your existing <strong>Initial Case Report</strong> and any{' '}
                <strong>Progress Reports</strong> in the Smart Import area above automatically extracts activity dates,
                numbers Progress Reports in chronological order, and configures these settings for you.
              </div>
              {onAddOngoingReportFiles && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-1 mt-sm-0"
                  onClick={onAddOngoingReportFiles}
                >
                  <i className="fas fa-file-upload mr-1" /> Add Existing Reports
                </button>
              )}
            </div>

            {/* Skip Initial Report Toggle */}
            <div className="form-row">
              <div className="col-md-6">
                <div className="form-group form-check mt-2">
                  <Field type="checkbox" name="skipInitialReport" className="form-check-input" id="skipInitialReport" />
                  <label className="form-check-label font-weight-semibold" htmlFor="skipInitialReport">
                    Skip Initial Case Report Auto-Generation
                  </label>
                  <small className="form-text text-muted d-block">
                    Check this if an Initial Case Report was already submitted for this case.
                  </small>
                </div>
              </div>
            </div>

            <div className="form-row">
              {/* Starting Progress Report Number */}
              <div className="col-md-3">
                <div className="form-group">
                  <label className="form-label mt-2" htmlFor="startingProgressReportNumber">
                    First Progress Report Sequence Number
                  </label>
                  <Field
                    type="number"
                    name="startingProgressReportNumber"
                    className="form-control"
                    id="startingProgressReportNumber"
                    min="1"
                  />
                  <ErrorMessage name="startingProgressReportNumber" />
                  <small className="form-text text-muted">
                    e.g., set to 5 if you want the next progress report to be &quot;Progress Report 5&quot;.
                  </small>
                </div>
              </div>

              {/* Starting Report Date */}
              <div className="col-md-3">
                <div className="form-group">
                  <label className="form-label mt-2" htmlFor="startingReportDate">
                    Next Report Starting Date
                  </label>
                  <Field name="startingReportDate">
                    {({ field }) => (
                      <DatePicker
                        id="startingReportDate"
                        dataFormat="YYYY-MM-DD"
                        {...field}
                        placeholderText="Defaults to day after last report"
                      />
                    )}
                  </Field>
                  <ErrorMessage name="startingReportDate" />
                  <small className="form-text text-muted">
                    Overrides the default referral date start range for billing and activity.
                  </small>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  )
}

export default ReferralForm
